import React, { useState, useCallback, useEffect, useRef } from "react";

import { getTotalStatistics } from "../../../services/dashboard";

import TotalsDisplayTile from "../../components/graphs/TotalsDisplayTile";
import { COCHARGE_DARK_GREEN } from "../../../styles/theme_constants";
import { projectToFullMonth, projectedChangePercent } from "../../../utils/monthExtrapolation";

const safeFixed = (val: unknown, digits: number): string => {
  const n = Number(val);
  return Number.isFinite(n) ? n.toFixed(digits) : "0";
};

const projectedDescription = (monthToDate: unknown, digits: number, unit: string) => {
  const n = Number(monthToDate);
  const projected = Number.isFinite(n) ? projectToFullMonth(n)?.toFixed(digits) ?? "–" : "0";
  return `extrapolated vs last month (≈ ${projected} ${unit})`;
};

const safeFixedOrNull = (val: number | null, digits = 2): string | null =>
  val == null ? null : safeFixed(val, digits);

const initialStats = {
  currentMonthMetrics: {
    totalRevenue: 0,
    totalEnergy: 0,
    sessionCount: 0,
    stationCount: 0,
  },
  monthOverMonthComparison: {
    revenueChangePercent: 0,
    energyChangePercent: 0,
    sessionCountChangePercent: 0,
    stationCountChangePercent: 0,
    previousPeriod: null as null | { totalEnergy: number; sessionCount: number },
  },
};

export const TotalStatTiles = ({ onLoaded }: { onLoaded?: () => void }) => {
  const [stats, setStats] = useState(initialStats);
  const notified = useRef(false); // used for loader display

  const getTilesInfo = useCallback(async () => {
    const data = (await getTotalStatistics()) || initialStats;
    setStats(data);
    if (!notified.current) {
      notified.current = true;
      onLoaded?.();
    }
  }, [onLoaded]);

  useEffect(() => {
    getTilesInfo();
  }, [getTilesInfo]);

  return (
    <div className="col-12">
      <div className="card">
        <div className="flex align-items-center mb-3">
          <i
            className="pi pi-chart-line"
            style={{ color: COCHARGE_DARK_GREEN, fontSize: "1.25rem" }}
          />
          <span className="text-xl font-semibold text-900 ml-2">
            Monthly Trends
          </span>
        </div>

        <div
          style={{
            borderTop: "1px solid var(--surface-border)",
            marginBottom: "1.25rem",
          }}
        />

        <div className="grid m-0">
          <div
            className="col-12 md:col-4"
            style={{ borderRight: "1px solid var(--surface-border)" }}
          >
            <TotalsDisplayTile
              accentColor="#3b82f6"
              unit="kWh"
              title="Energy Consumption"
              change={safeFixed(stats?.monthOverMonthComparison?.energyChangePercent, 2)}
              value={safeFixed(stats?.currentMonthMetrics?.totalEnergy, 3)}
              description="vs last month"
              projectedChange={safeFixedOrNull(
                projectedChangePercent(
                  stats?.currentMonthMetrics?.totalEnergy,
                  stats?.monthOverMonthComparison?.previousPeriod?.totalEnergy
                )
              )}
              projectedDescription={projectedDescription(
                stats?.currentMonthMetrics?.totalEnergy,
                0,
                "kWh"
              )}
            />
          </div>
          <div
            className="col-12 md:col-4"
            style={{ borderRight: "1px solid var(--surface-border)" }}
          >
            <TotalsDisplayTile
              accentColor="#84cc16"
              unit="count"
              title="Charging Sessions"
              change={safeFixed(stats?.monthOverMonthComparison?.sessionCountChangePercent, 2)}
              value={safeFixed(stats?.currentMonthMetrics?.sessionCount, 0)}
              description="vs last month"
              projectedChange={safeFixedOrNull(
                projectedChangePercent(
                  stats?.currentMonthMetrics?.sessionCount,
                  stats?.monthOverMonthComparison?.previousPeriod?.sessionCount
                )
              )}
              projectedDescription={projectedDescription(
                stats?.currentMonthMetrics?.sessionCount,
                0,
                "sessions"
              )}
            />
          </div>
          <div className="col-12 md:col-4">
            <TotalsDisplayTile
              accentColor="#f59e0b"
              unit="count"
              title="Active Charging Points"
              change={safeFixed(stats?.monthOverMonthComparison?.stationCountChangePercent, 2)}
              value={safeFixed(stats?.currentMonthMetrics?.stationCount, 0)}
              description="vs last month"
            />
          </div>
        </div>
      </div>
    </div>
  );
};
